import { useState } from 'react';

import type { ResponseSuccess } from 'Backend/users/useGetFactors';

import { Notification as PageNotification } from '../../../UI_Library/Notification';
import { useGetFactors } from 'Backend/users/useGetFactors';
import classes from './MfaOkta.module.css';
import LoadingBar from 'Components/UI_Library/LoadingIndicators/LoadingBar';
import Toggle from 'Components/UI_Library/FormElements/Toggle';
import { useEnrollFactor } from 'Backend/users/useEnrollFactor';
import { useUnenrollFactor } from 'Backend/users/useUnenrollFactor';
import SMSMFA from '../Components/SMSMFA';
import TOTPMFA from '../Components/TOTPMFA';
import { useGetUser } from 'Backend/users/getUser';
import * as MESSAGES from '../../../../Lib/Messages';
import { useActivateFactor } from 'Backend/users/useActivateFactor';
import Button from 'Components/UI_Library/FormElements/Button';

type FactorResponse = ResponseSuccess['factors'][0];

type MfaOptions = {
    SMS: MfaOption;
    TOTP: MfaOption;
};

type MfaOption = {
    factorId: string;
    code: string;
    isOptionOpened: boolean;
    isLoadingEnrollFactor: boolean;
    isLoadingActivateFactor: boolean;
};

const mapTypeToTitle: Record<FactorResponse['type'], string> = {
    SMS: 'SMS',
    TOTP: 'Authentication app',
};

const emptyMfaOption = {
    factorId: '',
    code: '',
    isLoadingEnrollFactor: false,
    isLoadingActivateFactor: false,
    isOptionOpened: false,
};

const MfaOkta = () => {
    const { data: factorsData, isLoading: isLoadingFactors, refetch: refetchFactors } = useGetFactors();
    const { mutateAsync: enrollFactor } = useEnrollFactor();
    const { mutateAsync: unenrollFactor, isLoading: isLoadingUnenrollFactor } = useUnenrollFactor();
    const { mutateAsync: activateFactor, error: activateFactorError } = useActivateFactor();
    const [totpQrCodeUrl, setTotpQrCodeUrl] = useState<string | undefined>();
    const { data: user, isFetching: isFetchingUser } = useGetUser();
    const [notificationMessage, setNotificationMessage] = useState<{ message: string; isError: boolean }>({
        message: '',
        isError: false,
    });

    const [mfaOptions, setMfaOptions] = useState<MfaOptions>({
        SMS: emptyMfaOption,
        TOTP: emptyMfaOption,
    });
    const [formErrors, setFormErrors] = useState<{ code?: string; TOTPCode?: string }>({});
    const [factorToDisable, setFactorToDisable] = useState<FactorResponse | undefined>();

    const isLoadingData = isLoadingFactors || isFetchingUser;

    const setMfaOption = (
        type: FactorResponse['type'],
        partial: Partial<MfaOptions['SMS']> | Partial<MfaOptions['TOTP']>
    ) => setMfaOptions((prev) => ({ ...prev, [type]: { ...prev[type], ...partial } }));

    const onToggleClick = async ({ type, status }: FactorResponse) => {
        // Cant deactivate SMS as it is required
        if (status === 'ENABLED' && type === 'SMS') {
            return;
        }

        if (status === 'ENABLED') {
            setFactorToDisable({ status, type });
            setMfaOption(type, { isOptionOpened: true });
            return;
        }

        setMfaOption(type, { isOptionOpened: !mfaOptions[type].isOptionOpened });

        if (type === 'TOTP') {
            try {
                setMfaOption('TOTP', { isLoadingEnrollFactor: true });
                const response = await enrollFactor({ factor: { type } });
                setTotpQrCodeUrl(response.qrCodeUrl);
                setMfaOption(type, { factorId: response.id });
            } finally {
                setMfaOption('TOTP', { isLoadingEnrollFactor: false });
            }
        }
    };

    const handleDisableMfaOption = async (type: FactorResponse['type']) => {
        await unenrollFactor({ type });
        await refetchFactors({ stale: true });
        setFactorToDisable(undefined);
        exitForm();
    };

    const handleVerifySmsCode = async () => {
        const { isValid, message } = isMfaFormValid('SMS');

        if (!isValid) {
            setFormErrors({
                code: message,
            });
        }

        setMfaOption('SMS', { isLoadingActivateFactor: true });

        try {
            await activateFactor({
                factor: { id: mfaOptions.SMS.factorId, type: 'SMS', passCode: mfaOptions.SMS.code },
            });
            await refetchFactors({ stale: true });
        } catch (error) {
            setNotificationMessage({
                message:
                    activateFactorError?.detail === 'The provided pass code was incorrect'
                        ? MESSAGES.ERROR_INVALID_CODE_MFA_SMS
                        : 'An errored activating your SMS MFA',
                isError: true,
            });
        } finally {
            setMfaOption('SMS', { isLoadingActivateFactor: false });
            setTimeout(() => setNotificationMessage({ message: '', isError: false }));
        }
    };

    const handleSendSmsCode = async () => {
        try {
            setMfaOption('SMS', { isLoadingEnrollFactor: true });
            const response = await enrollFactor({ factor: { type: 'SMS' } });

            setMfaOption('SMS', { factorId: response.id });
            setNotificationMessage({
                message: MESSAGES.SUCCESS_SEND_CODE_MFA_SMS,
                isError: false,
            });
        } catch {
            setNotificationMessage({
                message: MESSAGES.ERROR_SEND_CODE_MFA_SMS,
                isError: true,
            });
        } finally {
            setMfaOption('SMS', { isLoadingEnrollFactor: false });
            setTimeout(() => setNotificationMessage({ message: '', isError: false }));
        }
    };

    const isMfaFormValid = (type: FactorResponse['type']) => {
        if (!mfaOptions[type].code) {
            return {
                message: MESSAGES.ERROR_REQUIRED,
                isValid: false,
            };
        }
        if (mfaOptions[type].code.length !== 6) {
            return {
                message: MESSAGES.ERROR_NUMBER,
                isValid: false,
            };
        }

        return {
            isValid: true,
        };
    };

    const handleVerifyTotpCode = async () => {
        const { isValid, message } = isMfaFormValid('TOTP');

        if (!isValid) {
            setFormErrors({
                code: message,
            });
        }

        setMfaOption('TOTP', { isLoadingActivateFactor: true });

        try {
            await activateFactor({
                factor: { id: mfaOptions.TOTP.factorId, type: 'TOTP', passCode: mfaOptions.TOTP.code },
            });
            await refetchFactors();
        } catch {
            setNotificationMessage({
                message:
                    activateFactorError?.detail === 'The provided pass code was incorrect'
                        ? MESSAGES.ERROR_INVALID_CODE_MFA_TOTP
                        : 'An errored activating your Authentication app MFA',
                isError: true,
            });
        } finally {
            setMfaOption('TOTP', { isLoadingActivateFactor: false });
            setTimeout(() => setNotificationMessage({ message: '', isError: false }));
        }
    };

    const exitForm = () => {
        setMfaOption('SMS', { isOptionOpened: false });
        setMfaOption('TOTP', { isOptionOpened: false });
    };

    const handleCloseDisableNotification = () => setFactorToDisable(undefined);

    const displayMfaOptions = ({ title, factor }: { title: string; factor: FactorResponse }) => (
        <div key={factor.type} className={classes.mfaOption}>
            <div
                className={classes.mfaOptionToggleSection}
                data-testid={`toggle-option-${factor.type}`}
                onClick={() => onToggleClick(factor)}
            >
                <Toggle
                    size="L"
                    toggled={factor.status === 'ENABLED'}
                    disabled={
                        isLoadingData ||
                        mfaOptions[factor.type].isLoadingActivateFactor ||
                        mfaOptions[factor.type].isLoadingEnrollFactor ||
                        (factor.type === 'SMS' && factor.status === 'ENABLED') // Cant deactivate sms as it is required
                    }
                />
                <h5 className={classes.mfaOptionHeader}>{title}</h5>
            </div>
            {factor.type === 'SMS' && factor.status !== 'ENABLED' && mfaOptions[factor.type].isOptionOpened && (
                <div className={classes.mfaOptionInput}>
                    <SMSMFA
                        phoneNumber={user?.mobile ?? ''}
                        code={mfaOptions[factor.type].code}
                        loadingSMS={mfaOptions.SMS.isLoadingActivateFactor || mfaOptions.SMS.isLoadingEnrollFactor}
                        errors={formErrors}
                        exitForm={exitForm}
                        isFirstSend={false}
                        handleVerifySMSCode={handleVerifySmsCode}
                        handleSendSMSCode={handleSendSmsCode}
                        setCode={(code) => setMfaOption('SMS', { code })}
                    />
                </div>
            )}
            {factor.type === 'TOTP' && factor.status !== 'ENABLED' && mfaOptions[factor.type].isOptionOpened && (
                <div className={classes.mfaOptionInput}>
                    <TOTPMFA
                        TOTPCode={mfaOptions[factor.type].code}
                        isQrUrl
                        QRToken={totpQrCodeUrl}
                        loadingAuthApp={mfaOptions.TOTP.isLoadingActivateFactor}
                        isQrCodeLoading={mfaOptions.TOTP.isLoadingEnrollFactor}
                        errors={formErrors}
                        exitForm={exitForm}
                        handleVerifyTOTPCode={handleVerifyTotpCode}
                        setTOTPCode={(code) => setMfaOption('TOTP', { code })}
                    />
                </div>
            )}
        </div>
    );

    return (
        <div className={classes.container}>
            {isLoadingData && <LoadingBar />}
            {!isLoadingData && (
                <div className={classes.mfaOptionSection}>
                    {factorsData?.factors.map((factor) =>
                        displayMfaOptions({ title: mapTypeToTitle[factor.type], factor })
                    )}
                    <PageNotification
                        style={{ padding: 0, width: '80%', position: 'absolute' }}
                        isError={notificationMessage.isError}
                        message={notificationMessage.message}
                        handleCloseMsg={() => setNotificationMessage({ isError: false, message: '' })}
                    />
                </div>
            )}
            {factorToDisable?.status === 'ENABLED' && (
                <PageNotification
                    message={`Are you sure you want to disable ${mapTypeToTitle[factorToDisable.type]} MFA?`}
                    type="warning"
                    style={{ flex: 1, width: '100%', marginTop: '20px' }}
                    handleCloseMsg={() => handleCloseDisableNotification()}
                    buttons={
                        <Button
                            type="white-condensed"
                            text="Yes, disable it"
                            onClick={() => handleDisableMfaOption(factorToDisable.type)}
                            loading={isLoadingUnenrollFactor}
                            disabled={isLoadingUnenrollFactor}
                        />
                    }
                />
            )}
        </div>
    );
};

export default MfaOkta;
